import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MapPin, CalendarPlus, CalendarCheck, Check, X, Users, PartyPopper, Ticket } from "lucide-react";
import { getInvitationPublic, submitRsvp, passQrUrl } from "@/lib/api";
import { BRAND, LOGO_ICON } from "@/lib/contact";
import { getTheme } from "@/data/inviteThemes";

const ease = [0.16, 1, 0.3, 1];

function icsContent(inv) {
  const start = new Date(inv.event_date);
  const end = new Date(start.getTime() + 3 * 3600 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  return [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Zakhraf//Invite//AR", "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT", `UID:${inv.id}@zakhraf`, `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`,
    `SUMMARY:${(inv.title || "دعوة").replace(/\n/g, " ")}`,
    `LOCATION:${(inv.venue_name || inv.map_query || "").replace(/\n/g, " ")}`,
    `DESCRIPTION:${(inv.message || "").replace(/\n/g, " ")}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
}

function downloadIcs(inv) {
  const blob = new Blob([icsContent(inv)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${inv.title || "event"}.ics`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}

function fmtDate(iso) {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("ar", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
    time: d.toLocaleTimeString("ar", { hour: "2-digit", minute: "2-digit" }),
  };
}

function gcalLink(inv) {
  if (!inv.event_date) return "#";
  const start = new Date(inv.event_date);
  const end = new Date(start.getTime() + 3 * 3600 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: inv.title || "دعوة",
    dates: `${fmt(start)}/${fmt(end)}`,
    location: inv.venue_name || inv.map_query || "",
    details: inv.message || "",
  });
  return `https://www.google.com/calendar/render?${params.toString()}`;
}

function mapsLink(inv) {
  const q = inv.map_query || inv.venue_name || "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export default function InvitePage() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [modal, setModal] = useState(false);
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [pass, setPass] = useState(null);
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    getInvitationPublic(id).then(setInv).catch(() => setNotFound(true));
  }, [id]);

  const rsvp = async (status) => {
    if (!name.trim()) return toast.error("يرجى إدخال اسمك");
    setSending(true);
    try {
      const res = await submitRsvp(id, { guest_name: name.trim(), status });
      setModal(false);
      if (res.status === "accepted") {
        setPass(res);
        setTimeout(() => document.querySelector('[data-testid="entry-pass"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
      } else setDeclined(true);
    } catch {
      toast.error("تعذّر إرسال الرد، حاول مجددًا");
    } finally {
      setSending(false);
    }
  };

  const t = inv ? getTheme(inv.template_id) : getTheme("royal");

  if (notFound) return <div className="min-h-screen flex flex-col items-center justify-center text-white gap-3" style={{ background: t.bg }}><span className="text-5xl" style={{ color: t.accent }}>✦</span><p className="font-serif text-2xl">الدعوة غير موجودة</p></div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center font-serif text-2xl" style={{ background: t.bg, color: t.accent }}>جارٍ التحميل...</div>;

  const { date, time } = fmtDate(inv.event_date);
  const A = t.accent, TXT = t.text, SUB = t.sub;

  return (
    <div className="min-h-screen grain py-12 px-5" dir="rtl" data-testid="invite-page" style={{ background: t.bg }}>
      <div className="max-w-lg mx-auto relative z-10">
        {/* Invitation card */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease }} className="rounded-3xl overflow-hidden shadow-2xl backdrop-blur" style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${A}66` }}>
          <div className="p-8 sm:p-10 text-center gold-frame" style={{ color: TXT }}>
            <img src={LOGO_ICON} alt={BRAND} className="w-14 h-14 rounded-xl object-cover mx-auto mb-5" style={{ boxShadow: `0 0 0 1px ${A}80` }} />
            <div className="text-3xl mb-4" style={{ color: A }}>✦</div>
            {inv.hosts && <div className="text-sm mb-2" style={{ color: SUB }} data-testid="host-honorific">{inv.host_gender === "female" ? "تتشرّف" : "يتشرّف"}</div>}
            {inv.hosts && <div className="font-serif text-2xl mb-2">{inv.hosts}</div>}
            <div className="text-sm mb-4" style={{ color: SUB }}>بدعوتكم لحضور</div>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold leading-snug" style={{ color: A }}>{inv.title}</h1>

            <div className="h-px w-24 mx-auto my-7" style={{ background: A, opacity: 0.6 }} />

            {inv.message && <p className="leading-relaxed mb-6" style={{ color: SUB }}>{inv.message}</p>}

            <div className="space-y-3" style={{ color: TXT }}>
              {date && <div>{date}</div>}
              {time && <div style={{ color: A }}>{time}</div>}
              {inv.venue_name && <div className="flex items-center justify-center gap-2"><MapPin className="w-4 h-4" style={{ color: A }} /> {inv.venue_name}</div>}
            </div>

            <div className="mt-7 inline-flex items-center gap-2 rounded-full px-5 py-2" style={{ color: A, background: `${A}26`, border: `1px solid ${A}66` }}>
              <Users className="w-4 h-4" />
              <span data-testid="allowed-guests">عدد المدعوين: {inv.allowed_guests}</span>
            </div>
          </div>
        </motion.div>

        {/* Action buttons */}
        {!pass && !declined && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }} className="mt-6 space-y-3">
            <button onClick={() => setModal(true)} className="w-full rounded-2xl py-4 font-bold text-lg transition hover:brightness-105" style={{ background: A, color: "#1a1a1a" }} data-testid="rsvp-open-btn">
              تأكيد الحضور (RSVP)
            </button>
            <div className="grid grid-cols-2 gap-3">
              <a href={mapsLink(inv)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-2xl py-3.5 transition hover:brightness-110" style={{ color: TXT, border: `1px solid ${A}55` }} data-testid="location-btn">
                <MapPin className="w-4 h-4" /> الموقع
              </a>
              <a href={gcalLink(inv)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-2xl py-3.5 transition hover:brightness-110" style={{ color: TXT, border: `1px solid ${A}55` }} data-testid="calendar-btn">
                <CalendarPlus className="w-4 h-4" /> تقويم Google
              </a>
              <button onClick={() => downloadIcs(inv)} className="col-span-2 flex items-center justify-center gap-2 rounded-2xl py-3.5 transition hover:brightness-110" style={{ color: TXT, border: `1px solid ${A}55` }} data-testid="apple-calendar-btn">
                <CalendarCheck className="w-4 h-4" /> أضف لتقويم آيفون (Apple)
              </button>
            </div>
          </motion.div>
        )}

        {/* Declined message */}
        {declined && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-2xl p-8 text-center" style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${A}33`, color: TXT }} data-testid="declined-message">
            <div className="text-4xl mb-3" style={{ color: A }}>♥</div>
            <h3 className="font-serif text-2xl mb-2">شكرًا لإعلامنا</h3>
            <p style={{ color: SUB }}>سنفتقد حضورك، نتمنى لك كل الخير.</p>
          </motion.div>
        )}

        {/* Entry pass */}
        <AnimatePresence>
          {pass && (
            <motion.div initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.7, ease }} className="mt-6" data-testid="entry-pass">
              <div className="text-center mb-4" style={{ color: TXT }}>
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: `${A}33`, border: `1px solid ${A}` }}>
                  <PartyPopper className="w-8 h-8" style={{ color: A }} />
                </div>
                <h3 className="font-serif text-2xl font-bold">تم تأكيد حضورك!</h3>
                <p className="text-sm mt-1" style={{ color: SUB }}>هذه بطاقة دخولك الرقمية</p>
              </div>

              <div className="relative rounded-3xl bg-white overflow-hidden shadow-2xl">
                <div className="text-white p-6 flex items-center justify-between" style={{ background: t.bg }}>
                  <div className="flex items-center gap-2">
                    <Ticket className="w-5 h-5" style={{ color: A }} />
                    <span className="font-serif text-lg">بطاقة دخول</span>
                  </div>
                  <img src={LOGO_ICON} alt={BRAND} className="w-9 h-9 rounded-md object-cover" style={{ boxShadow: `0 0 0 1px ${A}80` }} />
                </div>
                <div className="relative h-6 bg-white">
                  <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-lav" />
                  <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full" style={{ background: t.bg }} />
                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full" style={{ background: t.bg }} />
                </div>
                <div className="p-6 text-center" style={{ color: "#232a44" }}>
                  <div className="text-xs mb-1" style={{ color: "#6c7189" }}>الضيف</div>
                  <div className="font-serif text-2xl font-bold mb-4" data-testid="pass-guest-name">{pass.guest_name}</div>
                  <div className="grid grid-cols-2 gap-4 text-right">
                    <div className="rounded-xl bg-lavender p-3">
                      <div className="text-xs" style={{ color: "#6c7189" }}>عدد المدعوين</div>
                      <div className="font-bold text-lg" data-testid="pass-guest-count">{pass.guests_count}</div>
                    </div>
                    <div className="rounded-xl bg-lavender p-3">
                      <div className="text-xs" style={{ color: "#6c7189" }}>رمز الدخول</div>
                      <div className="font-bold text-lg tracking-widest" data-testid="pass-code">{pass.pass_code}</div>
                    </div>
                  </div>
                  <div className="mt-5 pt-4 border-t border-lav">
                    <div className="text-xs mb-2" style={{ color: "#6c7189" }}>رمز الدخول السريع (QR)</div>
                    <img src={passQrUrl(pass.pass_code)} alt="QR" className="w-36 h-36 mx-auto rounded-lg border border-lav p-1 bg-white" data-testid="pass-qr" />
                    <div className="mt-1 text-[11px]" style={{ color: "#9aa0b4" }}>اعرضه عند الاستقبال للتحقق</div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-lav text-sm" style={{ color: "#6c7189" }}>
                    <div className="font-semibold" style={{ color: "#232a44" }}>{inv.title}</div>
                    {date && <div className="mt-1">{date} · {time}</div>}
                    {inv.venue_name && <div>{inv.venue_name}</div>}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <a href={mapsLink(inv)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-2xl py-3.5 transition hover:brightness-110" style={{ color: TXT, border: `1px solid ${A}55` }} data-testid="pass-location-btn">
                  <MapPin className="w-4 h-4" /> الموقع
                </a>
                <a href={gcalLink(inv)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-2xl py-3.5 transition hover:brightness-110" style={{ color: TXT, border: `1px solid ${A}55` }} data-testid="pass-calendar-btn">
                  <CalendarPlus className="w-4 h-4" /> تقويم Google
                </a>
                <button onClick={() => downloadIcs(inv)} className="col-span-2 flex items-center justify-center gap-2 rounded-2xl py-3.5 transition hover:brightness-110" style={{ color: TXT, border: `1px solid ${A}55` }} data-testid="pass-apple-calendar-btn">
                  <CalendarCheck className="w-4 h-4" /> أضف لتقويم آيفون (Apple)
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="text-center mt-8 text-xs" style={{ color: SUB }}>صُنع عبر <span style={{ color: A }}>{BRAND}</span></div>
      </div>

      {/* RSVP modal */}
      <AnimatePresence>
        {modal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/60 backdrop-blur-sm" onClick={() => setModal(false)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-3xl bg-white p-7" data-testid="rsvp-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-serif text-2xl font-bold" style={{ color: "#232a44" }}>تأكيد الحضور</h3>
                <button onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="rounded-xl bg-lavender px-4 py-3 mb-4 flex items-center gap-2 text-sm" style={{ color: "#3a4a78" }}>
                <Users className="w-4 h-4" /> عدد المدعوين المخصص لك: <b>{inv.allowed_guests}</b>
              </div>
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>الاسم الكامل</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="اكتب اسمك" className="w-full rounded-xl border border-lav px-4 py-3 text-right outline-none focus:border-primary mb-5" style={{ color: "#232a44" }} data-testid="rsvp-name-input" />
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => rsvp("accepted")} disabled={sending} className="flex items-center justify-center gap-2 rounded-xl text-white py-3.5 font-semibold disabled:opacity-60" style={{ background: t.bg }} data-testid="rsvp-accept-btn">
                  <Check className="w-4 h-4" /> سأحضر
                </button>
                <button onClick={() => rsvp("declined")} disabled={sending} className="flex items-center justify-center gap-2 rounded-xl border border-lav py-3.5 font-semibold disabled:opacity-60" style={{ color: "#6c7189" }} data-testid="rsvp-decline-btn">
                  <X className="w-4 h-4" /> أعتذر
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
